import { useEffect, useMemo, useRef, useState } from "react";
import { segmentUrl } from "../api.js";
import Icon from "./Icon.jsx";

const DRAG_MIME = "application/x-segment-tile";
const TILE_PX = 112;
const TILE_GAP_PX = 10;
const SLOT_PX = TILE_PX + TILE_GAP_PX;
const STAGGER_MS = 70;
// Oversample the visible count so there's always more to scroll to.
const OVERSAMPLE = 3;
const MIN_TILES = 18;

export default function Palette({
  tiles,
  loading,
  mode = "random",                // "random" | "favorites"
  onReshuffle,                    // dice click — App sets mode=random + reshuffles
  onShowFavorites,                // star click — App sets mode=favorites + loads list
  onTileDragStart,
  onSizeChange,
  isFavorite,
  onToggleFavorite,
  favoritesCount,
}) {
  const stripRef = useRef(null);
  const [diceSpinning, setDiceSpinning] = useState(false);

  useEffect(() => {
    const el = stripRef.current;
    if (!el || !onSizeChange) return;
    let lastN = 0;
    const recompute = () => {
      const w = el.clientWidth;
      // How many tiles fit at once; we ask for several times that many
      // so there's always more to scroll horizontally to.
      const visible = Math.max(1, Math.floor((w + TILE_GAP_PX) / SLOT_PX));
      const n = Math.max(MIN_TILES, visible * OVERSAMPLE);
      if (n !== lastN) { lastN = n; onSizeChange(n); }
    };
    recompute();
    const ro = new ResizeObserver(recompute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [onSizeChange]);

  // Per-render computation of stagger delays. We compare the current
  // `tiles` array to the last one we rendered with, and produce a
  // delay-per-slot only for slots whose tile id actually changed. This
  // gives a full cascade on dice presses (every slot changed) and a
  // snappy single-tile animation on a sibling refill (only one slot
  // changed, delay 0).
  const prevTilesRef = useRef([]);
  const animDelays = useMemo(() => {
    const prev = prevTilesRef.current;
    const changedSlots = [];
    for (let i = 0; i < tiles.length; i++) {
      const prevId = prev[i] ? prev[i].id : null;
      const currId = tiles[i] ? tiles[i].id : null;
      if (currId != null && prevId !== currId) changedSlots.push(i);
    }
    const delays = {};
    changedSlots.forEach((idx, order) => { delays[idx] = order * STAGGER_MS; });
    return delays;
  }, [tiles]);
  // Update prevTilesRef AFTER commit so the next render compares against
  // what we just drew, not the future.
  useEffect(() => { prevTilesRef.current = tiles; }, [tiles]);

  const handleShuffle = () => {
    setDiceSpinning(true);
    setTimeout(() => setDiceSpinning(false), 600);
    onReshuffle();
  };

  const isRandom = mode === "random";
  const isFavorites = mode === "favorites";

  return (
    <section className="flex h-full items-center gap-3 border-b-2 border-paper-edge bg-paper px-5">
      <button
        onClick={handleShuffle}
        disabled={loading}
        title={isRandom ? "Reshuffle the palette" : "Show random segments"}
        aria-pressed={isRandom}
        className={
          "group flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl border-2 border-ink transition hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-50 shadow-plump " +
          (isRandom ? "bg-cyan-bright" : "bg-paper hover:bg-paper-deep")
        }
        aria-label="Shuffle palette"
      >
        <Icon
          name="dice"
          size={48}
          alt="Shuffle"
          className={
            "transition group-hover:rotate-12 " +
            (diceSpinning ? "dice-shake" : "") +
            (isRandom ? "" : " opacity-70")
          }
        />
      </button>
      {onShowFavorites && (
        <button
          onClick={onShowFavorites}
          title={isFavorites ? "Currently showing favorites" : "Show favorited segments"}
          aria-pressed={isFavorites}
          className={
            "group relative flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl border-2 border-ink transition hover:-translate-y-0.5 active:translate-y-0 shadow-plump " +
            (isFavorites ? "bg-cyan-bright" : "bg-paper hover:bg-paper-deep")
          }
          aria-label="Show favorites"
        >
          <Icon
            name="star"
            size={42}
            alt=""
            className={"transition group-hover:rotate-6 " + (isFavorites ? "" : "opacity-70")}
          />
          {favoritesCount > 0 && (
            <span className="absolute -right-1.5 -top-1.5 flex h-6 min-w-6 items-center justify-center rounded-full border-2 border-ink bg-coral-soft px-1.5 text-[11px] font-bold tabular-nums text-ink">
              {favoritesCount}
            </span>
          )}
        </button>
      )}
      <div
        ref={stripRef}
        className="thin-scroll flex flex-1 items-center overflow-x-auto overflow-y-hidden py-1.5"
        style={{ gap: `${TILE_GAP_PX}px` }}
      >
        {isFavorites && tiles.filter((t) => t).length === 0 && (
          <div className="flex h-28 flex-1 items-center justify-center rounded-2xl border-2 border-dashed border-paper-edge bg-paper-deep/40 px-6 text-center text-sm text-ink-soft">
            No favorites yet — hover any segment in the palette and tap the
            <Icon name="star" size={20} alt="" className="mx-1 inline-block align-text-bottom" />
            to save it here.
          </div>
        )}
        {tiles.map((tile, idx) => {
          const delayMs = animDelays[idx];
          const animate = tile != null && delayMs != null;
          const key = tile ? `t-${tile.id}` : `slot-${idx}`;
          return (
            <Tile
              key={key}
              tile={tile}
              idx={idx}
              onDragStart={onTileDragStart}
              animate={animate}
              animDelayMs={animate ? delayMs : 0}
              isFavorite={tile && isFavorite ? isFavorite(tile.id) : false}
              onToggleFavorite={onToggleFavorite}
            />
          );
        })}
      </div>
    </section>
  );
}

function Tile({ tile, idx, onDragStart, animate, animDelayMs, isFavorite, onToggleFavorite }) {
  if (!tile) {
    return (
      <div className="flex h-28 w-28 shrink-0 items-center justify-center rounded-2xl border-2 border-dashed border-paper-edge bg-paper-deep/50 text-xs text-ink-faint">
        empty
      </div>
    );
  }
  const handleDragStart = (e) => {
    const payload = {
      tileIdx: idx,
      segmentId: tile.id,
      file: tile.file,
      sourceId: tile.sourceId,
      w: tile.w,
      h: tile.h,
    };
    e.dataTransfer.effectAllowed = "copy";
    e.dataTransfer.setData(DRAG_MIME, JSON.stringify(payload));
    e.dataTransfer.setData("text/plain", tile.file);
    if (onDragStart) onDragStart(idx, tile);
  };
  const handleToggleFavorite = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onToggleFavorite) onToggleFavorite(tile.id);
  };

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      title={`${tile.sourceName} · ${tile.w}×${tile.h}`}
      className={
        "group relative flex h-28 w-28 shrink-0 cursor-grab flex-col items-center justify-center rounded-2xl border-2 border-ink/15 bg-checker-sm p-1.5 transition hover:border-ink hover:-translate-y-0.5 active:cursor-grabbing shadow-card " +
        (animate ? "tile-drop" : "")
      }
      style={animate ? { animationDelay: `${animDelayMs}ms` } : undefined}
    >
      <img
        src={segmentUrl(tile.file)}
        alt={tile.file}
        className="pointer-events-none max-h-full max-w-full object-contain"
        draggable={false}
      />
      {onToggleFavorite && (
        <button
          onClick={handleToggleFavorite}
          onMouseDown={(e) => e.stopPropagation()}
          title={isFavorite ? "Unfavorite" : "Favorite"}
          aria-label={isFavorite ? "Unfavorite" : "Favorite"}
          className={
            "absolute right-1 top-1 flex h-7 w-7 items-center justify-center rounded-full border-2 border-ink transition " +
            (isFavorite
              ? "bg-cyan-soft opacity-100"
              : "bg-paper opacity-0 group-hover:opacity-100 hover:bg-cyan-soft")
          }
        >
          <Icon name="star" size={18} alt="" />
        </button>
      )}
    </div>
  );
}

export const PALETTE_DRAG_MIME = DRAG_MIME;
